import { useState, useRef, useEffect } from "react";
import CSVreader from "react-csv-reader";
import { json2csv } from "json-2-csv";
import dayjs from "dayjs";

import {
  Box,
  FormControlLabel,
  GlobalStyles,
  Grid,
  Switch,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  TextField,
  StyledEngineProvider,
  ThemeProvider,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRight } from "@fortawesome/free-solid-svg-icons";
import { faPodiumStar } from "@fortawesome/pro-solid-svg-icons";

import { Select } from "@aclymatepackages/atoms";
import { PlacesAutocomplete as PlacesAutocompleteModule } from "@aclymatepackages/modules";
import { Autocomplete } from "@aclymatepackages/atoms";
import {
  mainTheme,
  mergeMainTheme,
  mergeDarkTheme
} from "@aclymatepackages/themes";

import MultiPartFormReveal from "../../inputs/forms/MultiPartFormReveal";
import Backdrop from "../../atoms/mui/Backdrop";
import AirportAutocomplete from "../../inputs/autocomplete/AirportAutocomplete";

import { MultipartFormContext } from "../../../helpers/contexts/multiPartForm";
import { useInternalAdminContext } from "../../../helpers/contexts/internalAdmin";
import { uploadFileToStorage, useAuth } from "../../../helpers/firebase";
import { eventAttendeesFieldOptions } from "../../../helpers/components/tasks";
import { findGoogleAddressStringDetails } from "../../../helpers/utils/geography";
import {
  fetchNearestAirportFromCoordinates,
  fetchOurApi
} from "../../../helpers/utils/apiCalls";

const knownFields = [
  { header: "city", field: "city" },
  { header: "state", field: "state" },
  { header: "zip code", field: "zipCode" },
  { header: "zipcode", field: "zipCode" },
  { header: "postal code", field: "zipCode" },
  { header: "country", field: "country" },
  { header: "transportation method", field: "transportationMethod" },
  { header: "transportationmethod", field: "transportationMethod" },
  { header: "transport", field: "transportationMethod" },
  { header: "transport method", field: "transportationMethod" }
];

const requiredFields = ["city"];

const allFieldOptions = [
  { label: "None", value: "none" },
  ...eventAttendeesFieldOptions.map((opt) =>
    requiredFields.includes(opt.value)
      ? { ...opt, label: `${opt.label} (required)` }
      : opt
  )
];

const HeaderRowSelector = ({ csvData, headerIdx, setHeaderIdx }) => {
  const startIdx = Math.max(0, headerIdx - 3);
  const endIdx = Math.min(csvData.length, headerIdx + 4);
  const visibleRows = csvData.slice(startIdx, endIdx);

  return (
    <Grid container direction="column" spacing={2} alignItems="center">
      <Grid
        item
        style={{ overflowX: "auto", overflowY: "hidden", width: "100%" }}
      >
        <Table size="small">
          <TableBody>
            {visibleRows.map((row, idx) => {
              const actualIdx = startIdx + idx;
              const isSelected = actualIdx === headerIdx;

              return (
                <StyledEngineProvider
                  injectFirst
                  key={`header-row-${actualIdx}`}
                >
                  <ThemeProvider
                    theme={isSelected ? mainTheme : mergeDarkTheme}
                  >
                    <TableRow
                      hover
                      selected={isSelected}
                      onClick={() => setHeaderIdx(actualIdx)}
                      style={{
                        cursor: "pointer",
                        backgroundColor: isSelected ? "white" : "inherit"
                      }}
                    >
                      <TableCell>
                        {isSelected ? (
                          <FontAwesomeIcon icon={faArrowRight} size="lg" />
                        ) : (
                          ""
                        )}
                      </TableCell>
                      <TableCell style={isSelected ? { fontWeight: 700 } : {}}>
                        {actualIdx}
                      </TableCell>
                      {row.map((cell, cellIdx) => (
                        <TableCell
                          key={`cell-${actualIdx}-${cellIdx}`}
                          style={isSelected ? { fontWeight: 700 } : {}}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  </ThemeProvider>
                </StyledEngineProvider>
              );
            })}
          </TableBody>
        </Table>
      </Grid>
      <Grid item>
        <Typography>
          {`Confirm that row ${headerIdx} contains the headers for your table?`}
        </Typography>
      </Grid>
    </Grid>
  );
};

const ColumnMapper = ({
  csvData,
  headerIdx,
  matchedHeaders,
  setMatchedHeaders
}) => {
  const dataRows = csvData
    .slice(headerIdx + 1, headerIdx + 4)
    .map((row, idx) => ({ data: row, id: headerIdx + 1 + idx }));

  const onFieldSelect = (colIdx) => (field) => {
    const updated = matchedHeaders.map((header, idx) => {
      if (idx === colIdx) return { ...header, field };
      if (header.field === field && field !== "none")
        return { ...header, field: "none" };
      return header;
    });
    setMatchedHeaders(updated);
  };

  return (
    <Box style={{ overflowX: "auto", overflowY: "hidden", width: "100%" }}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={mainTheme}>
          <Table size="small">
            <TableHead style={{ backgroundColor: "white" }}>
              <TableRow style={{ verticalAlign: "top" }}>
                {matchedHeaders.map((header, idx) => (
                  <TableCell key={`map-header-${idx}`}>
                    <Box py={1}>
                      <Select
                        options={allFieldOptions}
                        value={header.field}
                        editValue={onFieldSelect(idx)}
                        size="small"
                      />
                    </Box>
                    {header.name}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dataRows.map(({ data, id }) => (
                <StyledEngineProvider injectFirst key={`data-row-${id}`}>
                  <ThemeProvider theme={mergeDarkTheme}>
                    <TableRow>
                      {data
                        .filter((_, idx) => idx < matchedHeaders.length)
                        .map((cell, idx) => (
                          <TableCell key={`data-cell-${id}-${idx}`}>
                            {cell}
                          </TableCell>
                        ))}
                    </TableRow>
                  </ThemeProvider>
                </StyledEngineProvider>
              ))}
            </TableBody>
          </Table>
        </ThemeProvider>
      </StyledEngineProvider>
    </Box>
  );
};

const AdminEventUploadForm = ({ open, onClose }) => {
  const { palette } = useTheme();
  const { selectableCompanies } = useInternalAdminContext();
  const [user] = useAuth();
  const formBottomRef = useRef();

  // Form state -- pre-select the user's company if available
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [addingNewCompany, setAddingNewCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState("");
  const [newCompanyDescription, setNewCompanyDescription] = useState("");
  const [creatingCompany, setCreatingCompany] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);

  useEffect(() => {
    if (!selectedCompany && selectableCompanies.length && user) {
      user.getIdTokenResult().then(({ claims }) => {
        const match = selectableCompanies.find((c) => c.id === claims.company);
        if (match) setSelectedCompany(match);
      });
    }
  }, [selectableCompanies, user, selectedCompany]);

  // Create a new company using the existing vendor creation endpoint. Called
  // directly via fetchOurApi (bypassing the shared fetchCompanyDataFromName
  // helper) since that helper's callback discards everything but companyData
  // -- this needs the full response to see requiresConfirmation.
  const handleCreateCompany = async (mergeDecision) => {
    if (!newCompanyName.trim()) return;
    setCreatingCompany(true);
    setDuplicateWarning(null);
    try {
      const response = await fetchOurApi({
        path: "/companies/fetch-company-data-from-name",
        method: "POST",
        data: {
          name: newCompanyName.trim(),
          description: newCompanyDescription.trim(),
          checkForDuplicates: true,
          ...(mergeDecision ? { mergeDecision } : {}),
          ...(mergeDecision === "merge" && duplicateWarning
            ? { confirmedMatchedCompanyId: duplicateWarning.matchedCompanyId }
            : {})
        },
        callback: (response) => response
      });

      if (response?.requiresConfirmation) {
        setDuplicateWarning({
          matchedCompanyId: response.matchedCompanyId,
          matchedCompanyName: response.matchedCompanyName
        });
        return;
      }

      const companyData = response?.companyData;
      if (companyData?.id) {
        setSelectedCompany({
          id: companyData.id,
          name: companyData.name || newCompanyName.trim()
        });
        setAddingNewCompany(false);
        setNewCompanyName("");
        setNewCompanyDescription("");
        setDuplicateWarning(null);
      }
    } catch (err) {
      console.error("Failed to create company:", err);
    } finally {
      setCreatingCompany(false);
    }
  };
  const [eventPlace, setEventPlace] = useState(null);
  const [eventLocation, setEventLocation] = useState(null);
  const [eventAirport, setEventAirport] = useState(null);
  const [loadingAirport, setLoadingAirport] = useState(false);
  const [defaultCountry, setDefaultCountry] = useState("");
  const [csvData, setCsvData] = useState([]);
  const [fileInfo, setFileInfo] = useState({});
  const [headerIdx, setHeaderIdx] = useState(0);
  const [matchedHeaders, setMatchedHeaders] = useState([]);
  const [originalFileObj, setOriginalFileObj] = useState(null);
  const [isTrainEvent, setIsTrainEvent] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const goToBottom = () => {
    formBottomRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "end",
      inline: "nearest"
    });
  };

  // Handle place selection and auto-find nearest airport
  const handlePlaceSelect = async (place) => {
    setEventPlace(place);

    if (!place) {
      setEventLocation(null);
      setEventAirport(null);
      return;
    }

    const { place_id, description, formatted_address } = place;
    const address = await findGoogleAddressStringDetails(place_id);
    setEventLocation({
      ...address,
      description: description || formatted_address,
      placeId: place_id
    });

    setLoadingAirport(true);
    const airport = await fetchNearestAirportFromCoordinates(
      address.coordinates
    );
    setEventAirport(airport);
    setLoadingAirport(false);
  };

  // Handle CSV file upload
  const onCsvUpload = (data, fileInfoObj, originalFile) => {
    setCsvData(data);
    setFileInfo(fileInfoObj);
    setOriginalFileObj(originalFile);

    // Auto-detect header row
    const knownHeaderNames = knownFields.map((f) => f.header.toLowerCase());
    const scores = data.map(
      (row) =>
        row.filter((cell) =>
          knownHeaderNames.includes(String(cell).toLowerCase())
        ).length
    );
    const maxScore = Math.max(...scores);
    const detectedIdx = maxScore > 0 ? scores.indexOf(maxScore) : 0;
    setHeaderIdx(detectedIdx);
  };

  // Process header row into matched headers
  const processHeaderRow = () => {
    const headerRow = csvData[headerIdx];
    const truncatedHeaders = headerRow.filter((h) => h);

    const matched = truncatedHeaders.map((header) => {
      const knownField = knownFields.find(
        (f) => f.header.toLowerCase() === header.toLowerCase()
      );
      return { name: header, field: knownField?.field || "none" };
    });

    // Deduplicate: if two headers match the same field, keep first
    const deduped = matched.map((header, idx, self) => {
      if (header.field === "none") return header;
      const firstIdx = self.findIndex((h) => h.field === header.field);
      return firstIdx !== idx ? { ...header, field: "none" } : header;
    });

    setMatchedHeaders(deduped);
  };

  // Validate column mapping
  const validateMapping = () => {
    const mappedFields = matchedHeaders
      .filter((h) => h.field !== "none")
      .map((h) => h.field);

    const required = ["city"];
    const missing = required.filter((f) => !mappedFields.includes(f));

    // Country is satisfied by either a mapped CSV column or the default country form field
    if (!mappedFields.includes("country") && !defaultCountry.trim()) {
      missing.push("country");
    }

    if (missing.length > 0) {
      return {
        success: false,
        message: missing.includes("country")
          ? `Missing required fields: ${missing.join(", ")}. Map a country column or set a default country above.`
          : `Missing required fields: ${missing.join(", ")}`
      };
    }
    return { success: true };
  };

  // Submit the form
  const handleSubmit = async () => {
    const validation = validateMapping();
    if (!validation.success) {
      setSubmitError(validation.message);
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");

    try {
      const { uid } = user;
      const { name: fileName } = fileInfo;
      const companyId = selectedCompany.id;

      const filteredHeaders = matchedHeaders.filter((h) => h.field !== "none");

      // Build formatted CSV data
      const headerRow = csvData[headerIdx];
      const dataRows = csvData.slice(headerIdx + 1);

      // Check if the user mapped a country column in the CSV
      const countryMapped = filteredHeaders.some((h) => h.field === "country");

      // Include all original columns; inject default country if no country column was mapped
      const formattedData = dataRows
        .filter((row) => row.some((cell) => cell))
        .map((row) => {
          const obj = {};
          headerRow.forEach((header, colIdx) => {
            const key = header || `column_${colIdx + 1}`;
            obj[key] = row[colIdx] || "";
          });
          if (!countryMapped && defaultCountry.trim()) {
            obj["country"] = defaultCountry.trim();
          }
          return obj;
        });

      const csv = json2csv(formattedData);
      const csvBlob = new Blob([csv], { type: "text/csv" });

      const storagePath = `${companyId}/csv/events/${dayjs().format(
        "MM-DD-YYYY--"
      )}${fileName.split(" ").join("_")}`;

      // Upload raw file
      await uploadFileToStorage({
        path: `${storagePath}/raw-data.csv`,
        fileObject: originalFileObj,
        hasUserConfirmedUpload: true
      });

      // Build column mapping metadata
      const columnMappingMetadata = filteredHeaders.reduce(
        (acc, { field, name }) => ({ ...acc, [field]: name }),
        {}
      );
      // If country was injected from the default form field, tell the backend the column name is "country"
      if (!countryMapped && defaultCountry.trim()) {
        columnMappingMetadata.country = "country";
      }

      // Upload processed file with metadata
      await uploadFileToStorage({
        path: `${storagePath}/processed-${fileName.split(" ").join("_")}`,
        fileObject: csvBlob,
        fileMetadata: {
          customMetadata: {
            fileName,
            dateLoaded: new Date().toISOString(),
            userId: uid,
            companyId,
            companyName: selectedCompany.name,
            initiatedByEmail: user.email || "",
            initiatedByName: user.displayName || "",
            eventLocation: JSON.stringify(eventLocation),
            eventAirport: JSON.stringify({
              latitude: eventAirport.latitude,
              longitude: eventAirport.longitude
            }),
            isTrainEvent: String(isTrainEvent),
            ...columnMappingMetadata
          }
        },
        hasUserConfirmedUpload: true
      });

      onClose({
        companyName: selectedCompany.name,
        companyId: selectedCompany.id,
        status: "uploading",
        type: "event-attendees-csv",
        filePath: `${storagePath}/processed-data.csv`,
        startTime: new Date()
      });
    } catch (err) {
      console.error("Failed to upload event CSV:", err);
      setSubmitError("Upload failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step definitions
  const forms = [
    {
      label: "Select Company",
      title: "Which company is this event for?",
      input: addingNewCompany ? (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={mergeDarkTheme}>
                <TextField
                  label="Company name"
                  value={newCompanyName}
                  onChange={(e) => {
                    setNewCompanyName(e.target.value);
                    setDuplicateWarning(null);
                  }}
                  size="small"
                  fullWidth
                />
              </ThemeProvider>
            </StyledEngineProvider>
          </Grid>
          <Grid item>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={mergeDarkTheme}>
                <TextField
                  label="Description (what the company does)"
                  value={newCompanyDescription}
                  onChange={(e) => {
                    setNewCompanyDescription(e.target.value);
                    setDuplicateWarning(null);
                  }}
                  size="small"
                  fullWidth
                />
              </ThemeProvider>
            </StyledEngineProvider>
          </Grid>
          {duplicateWarning && (
            <Grid item>
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={mergeDarkTheme}>
                  <Typography variant="body2" color="textPrimary">
                    A similar company already exists: &quot;
                    {duplicateWarning.matchedCompanyName}&quot;. Is this the same
                    company?
                  </Typography>
                </ThemeProvider>
              </StyledEngineProvider>
            </Grid>
          )}
          <Grid item container spacing={1}>
            {duplicateWarning ? (
              <>
                <Grid item>
                  <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={mergeMainTheme}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                          setSelectedCompany({
                            id: duplicateWarning.matchedCompanyId,
                            name: duplicateWarning.matchedCompanyName
                          });
                          setAddingNewCompany(false);
                          setNewCompanyName("");
                          setNewCompanyDescription("");
                          setDuplicateWarning(null);
                        }}
                      >
                        Yes, use this company
                      </Button>
                    </ThemeProvider>
                  </StyledEngineProvider>
                </Grid>
                <Grid item>
                  <Button
                    variant="outlined"
                    onClick={() => handleCreateCompany("new")}
                    disabled={creatingCompany}
                    style={{ color: "rgba(255,255,255,0.87)" }}
                  >
                    {creatingCompany ? "Creating..." : "No, create separately"}
                  </Button>
                </Grid>
              </>
            ) : (
              <>
                <Grid item>
                  <StyledEngineProvider injectFirst>
                    <ThemeProvider theme={mergeMainTheme}>
                      <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => handleCreateCompany()}
                        disabled={!newCompanyName.trim() || creatingCompany}
                      >
                        {creatingCompany ? "Creating..." : "Create Company"}
                      </Button>
                    </ThemeProvider>
                  </StyledEngineProvider>
                </Grid>
                <Grid item>
                  <Button
                    size="small"
                    onClick={() => {
                      setAddingNewCompany(false);
                      setDuplicateWarning(null);
                    }}
                    style={{
                      color: "rgba(255,255,255,0.7)",
                      textTransform: "none"
                    }}
                  >
                    Back to list
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
      ) : (
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <Autocomplete
              value={selectedCompany}
              setValue={setSelectedCompany}
              availableOptions={selectableCompanies}
              label="Select a company"
              size="small"
            />
          </Grid>
          <Grid item>
            <Button
              size="small"
              onClick={() => setAddingNewCompany(true)}
              style={{ color: "rgba(255,255,255,0.7)", textTransform: "none" }}
            >
              Company not in the list? Add new
            </Button>
          </Grid>
        </Grid>
      ),
      buttonDisabled: !selectedCompany || creatingCompany
    },
    {
      label: "Event Location",
      title: "Where was this event located?",
      subtitle:
        "Select the event location. The nearest airport will be found automatically.",
      input: (
        <Grid container direction="column" spacing={2}>
          <Grid item>
            <PlacesAutocompleteModule
              label="Event Location"
              place={eventPlace}
              editPlace={handlePlaceSelect}
              size="small"
            />
          </Grid>
          {eventPlace && (
            <Grid item>
              <AirportAutocomplete
                label="Event Airport"
                airport={eventAirport}
                editValue={(newAirport) => {
                  if (newAirport) setEventAirport(newAirport);
                }}
                isInternational
              />
              {!loadingAirport && eventAirport && (
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ marginTop: 4, display: "block" }}
                >
                  Auto-detected nearest airport. Search to change if needed.
                </Typography>
              )}
            </Grid>
          )}
          <Grid item>
            <FormControlLabel
              control={
                <Switch
                  checked={isTrainEvent}
                  onChange={(e) => setIsTrainEvent(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2" color="textSecondary">
                  Include train emissions (Northeast Corridor events)
                </Typography>
              }
            />
          </Grid>
        </Grid>
      ),
      buttonDisabled: !eventLocation || !eventAirport || loadingAirport
    },
    {
      label: "Upload CSV",
      title: "Upload your event attendees CSV",
      subtitle:
        "Required: city. Optional: state, country, zip code, transportation method. Set a default country below if your CSV has no country column.",
      input: (
        <>
          <Box style={{ width: "100%" }}>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={mergeDarkTheme}>
                <TextField
                  label="Default attendee country (optional)"
                  value={defaultCountry}
                  onChange={(e) => setDefaultCountry(e.target.value)}
                  size="small"
                  fullWidth
                  helperText="Applied to all rows if no country column is mapped"
                />
              </ThemeProvider>
            </StyledEngineProvider>
          </Box>
          <Box p={2}>
            <Grid container alignItems="center" direction="column" spacing={2}>
              <Grid item>
                <StyledEngineProvider injectFirst>
                  <ThemeProvider theme={mergeMainTheme}>
                    <Button
                      variant="contained"
                      color="secondary"
                      id="admin-csv-upload-btn"
                    >
                      <label htmlFor="admin-csv-upload-input">
                        Upload a file
                      </label>
                    </Button>
                  </ThemeProvider>
                </StyledEngineProvider>
                <CSVreader
                  onFileLoaded={(data, info, originalFile) =>
                    onCsvUpload(data, info, originalFile)
                  }
                  inputId="admin-csv-upload-input"
                  inputStyle={{ display: "none" }}
                />
              </Grid>
              {fileInfo.name && (
                <Grid item>
                  <Grid
                    container
                    alignItems="center"
                    spacing={1}
                    justifyContent="center"
                  >
                    <Grid item>
                      <Typography variant="body2">{fileInfo.name}</Typography>
                    </Grid>
                    <Grid item>
                      <Button
                        size="small"
                        onClick={() => {
                          setCsvData([]);
                          setFileInfo({});
                          setOriginalFileObj(null);
                          setHeaderIdx(0);
                          setMatchedHeaders([]);
                        }}
                        style={{
                          color: "rgba(255,255,255,0.7)",
                          textTransform: "none"
                        }}
                      >
                        Remove
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      ),
      buttonDisabled: csvData.length === 0 || !fileInfo.name
    },
    {
      label: "Confirm Header Rows",
      size: "lg",
      title:
        "We need you to confirm the row in your CSV that contains the headers for your columns",
      input: csvData.length > 0 && (
        <HeaderRowSelector
          csvData={csvData}
          headerIdx={headerIdx}
          setHeaderIdx={setHeaderIdx}
        />
      ),
      buttonText: "confirm",
      onAdvanceForm: processHeaderRow
    },
    {
      label: "Map Header Columns",
      size: "lg",
      title:
        'Match your headers to the required column (city) and any optional columns (state, country, zip code, transportation method). Choose "None" for columns to ignore.',
      input: matchedHeaders.length > 0 && (
        <Grid container direction="column" spacing={2} alignItems="center">
          <Grid item style={{ width: "100%" }}>
            <ColumnMapper
              csvData={csvData}
              headerIdx={headerIdx}
              matchedHeaders={matchedHeaders}
              setMatchedHeaders={setMatchedHeaders}
            />
          </Grid>
          {submitError && (
            <Grid item>
              <Typography color="error" variant="body2">
                {submitError}
              </Typography>
            </Grid>
          )}
          <Grid item>
            <StyledEngineProvider injectFirst>
              <ThemeProvider theme={mergeMainTheme}>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isSubmitting || !validateMapping().success}
                  onClick={handleSubmit}
                >
                  {isSubmitting ? "Uploading..." : "Submit"}
                </Button>
              </ThemeProvider>
            </StyledEngineProvider>
          </Grid>
        </Grid>
      ),
      noButton: true
    }
  ];

  if (!open) return null;

  return (
    <MultipartFormContext.Provider
      value={{ color: palette.primary.main, formBottomRef, goToBottom }}
    >
      <GlobalStyles styles={{ ".MuiAutocomplete-popper": { zIndex: 1400 } }} />
      <Backdrop
        open
        onClose={onClose}
        theme={mergeDarkTheme}
        style={{
          zIndex: 1300,
          color: "white",
          overflowY: "auto",
          overflowX: "hidden"
        }}
        boxStyle={{ padding: 0, width: "100%" }}
      >
        <Box
          style={{
            height: "100%",
            width: "100%",
            boxSizing: "content-box",
            overflow: "hidden"
          }}
          position="relative"
          display="flex"
          alignItems="center"
          flexDirection="column"
        >
          <Box
            flexGrow={1}
            p={4}
            style={{
              maxHeight: "100%",
              boxSizing: "border-box",
              overflowY: "auto",
              overflowX: "hidden",
              width: "100%"
            }}
          >
            <Grid container direction="column" spacing={4}>
              <Grid
                item
                container
                direction="column"
                spacing={1}
                alignItems="center"
              >
                <Grid item>
                  <FontAwesomeIcon
                    icon={faPodiumStar}
                    size="3x"
                    style={{ color: "white" }}
                  />
                </Grid>
                <Grid item>
                  <Typography variant="h3" color="inherit" align="center">
                    Upload Event Attendees CSV
                  </Typography>
                </Grid>
              </Grid>
              <MultiPartFormReveal forms={forms} />
              <div ref={formBottomRef} />
            </Grid>
          </Box>
        </Box>
      </Backdrop>
    </MultipartFormContext.Provider>
  );
};

export default AdminEventUploadForm;
